<template>
  <v-container fluid>
    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <!-- 🔹 LISTA DE TABLONES -->
    <div v-for="board in boards" :key="board.id">
      <v-card v-if="isMobile" class="mobile-board-panel" outlined>
        <div class="mobile-board-header">
          <div>
            <h2 class="mobile-board-title">{{ board.title }}</h2>
            <div class="mobile-board-count">
              {{ board.notices.length }} {{ $t('notices.title').toLowerCase() }}
            </div>
          </div>

          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" class="mobile-board-menu">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item @click="editBoard(board)">
                <v-list-item-title>
                  <v-icon small class="pr-2">mdi-pencil</v-icon>{{ $t('common.edit') }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item @click="deleteBoard(board.id)">
                <v-list-item-title>
                  <v-icon small color="red" class="pr-2">mdi-delete</v-icon>{{ $t('common.delete') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div class="mobile-board-actions">
          <v-btn small color="primary" dark class="mobile-board-action mobile-board-action--primary" @click="openNoticeDialog(board.id)">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t('notices.newNotice') }}
          </v-btn>
        </div>

        <div v-if="!board.notices.length" class="notice-empty-mobile">
          {{ $t('notices.emptyBoard') }}
        </div>

        <div v-else class="mobile-notice-list">
          <article
            v-for="notice in board.notices"
            :key="notice.id"
            class="mobile-notice-item"
          >
            <div class="mobile-notice-row">
              <div class="mobile-notice-content">
                <div class="mobile-notice-date">{{ formatDate(notice.date) }}</div>
                <h3 class="mobile-notice-title">{{ notice.title }}</h3>
                <p class="mobile-notice-description">{{ notice.description }}</p>
              </div>

              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" class="mobile-notice-menu">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item
                    v-if="notice.description.length > 120"
                    @click="openViewNotice(notice)"
                  >
                    <v-list-item-title>
                      <v-icon small class="pr-2">mdi-eye</v-icon>{{ $t('notices.seeMore') }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="editNotice(board.id, notice)">
                    <v-list-item-title>
                      <v-icon small class="pr-2">mdi-pencil</v-icon>{{ $t('common.edit') }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="deleteNotice(board.id, notice.id)">
                    <v-list-item-title>
                      <v-icon small color="red" class="pr-2">mdi-delete</v-icon>{{ $t('common.delete') }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </article>
        </div>
      </v-card>

      <template v-else>
        <div class="d-flex align-center justify-space-between mb-3 pa-0 board-header">
          <h2 class="board-title">📌 {{ $t('notices.title') }} {{ board.title }}</h2>

          <div class="d-flex align-center board-actions">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="action-btn">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="editBoard(board)">
                  <v-list-item-title>
                    <v-icon small class="pr-2">mdi-pencil</v-icon>{{ $t('common.edit') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item @click="deleteBoard(board.id)">
                  <v-list-item-title>
                    <v-icon small color="red" class="pr-2">mdi-delete</v-icon>{{ $t('common.delete') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              small
              color="primary"
              dark
              class="font-weight-medium ml-2 rounded-lg"
              @click="openNoticeDialog(board.id)"
            >
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('notices.newNotice') }}
            </v-btn>
          </div>
        </div>

        <v-slide-group show-arrows class="py-2 notice-slider">
          <v-slide-item v-for="notice in board.notices" :key="notice.id">
            <v-card class="pa-4 mx-3 notice-card" outlined>
              <div class="d-flex align-center mb-3 notice-card-header">
                <v-avatar color="primary" size="44" class="mr-3">
                  <v-icon color="white" size="26">mdi-bulletin-board</v-icon>
                </v-avatar>

                <div class="flex-grow-1 notice-card-heading">
                  <h3 class="notice-title">{{ notice.title }}</h3>
                  <small class="text-grey">{{ formatDate(notice.date) }}</small>
                </div>

                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" class="action-btn">
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense>
                    <v-list-item @click="editNotice(board.id, notice)">
                      <v-list-item-title>
                        <v-icon small class="pr-2">mdi-pencil</v-icon>{{ $t('common.edit') }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteNotice(board.id, notice.id)">
                      <v-list-item-title>
                        <v-icon small color="red" class="pr-2">mdi-delete</v-icon>{{ $t('common.delete') }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <p class="text-body-2 notice-description">
                {{ notice.description }}
              </p>

              <div v-if="notice.description.length > 120">
                <v-btn
                  x-small
                  text
                  class="see-more-btn"
                  @click="openViewNotice(notice)"
                >
                  {{ $t('notices.seeMore') }}
                </v-btn>
              </div>
            </v-card>
          </v-slide-item>
        </v-slide-group>

        <v-divider class="my-8"></v-divider>
      </template>
    </div>

    <!-- 🔹 BOTÓN NUEVO TABLÓN -->
    <v-btn
      color="primary"
      fab
      dark
      fixed
      bottom
      right
      class="elevation-6"
      data-tour="notices-create-board"
      @click="dialogBoard = true"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <!-- 🔹 MODAL TABLÓN -->
    <v-dialog v-model="dialogBoard" max-width="420px">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          {{ editingBoard ? $t('notices.editBoard') : $t('notices.newBoard') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="boardForm" v-model="boardValid">
          <v-text-field
            v-model="newBoardTitle"
            :label="$t('notices.boardTitle')"
            outlined
            dense
            :rules="[rules.required]"
            required
          ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="closeBoardDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveBoard">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 🔹 MODAL AVISO -->
    <v-dialog v-model="dialogNotice" max-width="600px">
  <v-card class="dialog-scroll">
    <v-card-title class="text-h6 font-weight-bold">
      {{ editingNotice ? $t('notices.editNotice') : $t('notices.newNotice') }}
    </v-card-title>

    <v-card-text class="dialog-content">
      <v-form ref="noticeForm" v-model="noticeValid">
      <v-text-field
        v-model="newNoticeTitle"
        :label="$t('notices.noticeTitle')"
        outlined
        dense
        class="mb-4"
        :rules="[rules.required]"
        required
      ></v-text-field>

      <v-textarea
        v-model="newNoticeDescription"
        :label="$t('notices.description')"
        outlined
        rows="5"
        class="scroll-textarea"
        :rules="[rules.required]"
        required
      ></v-textarea>
      </v-form>
    </v-card-text>

    <v-card-actions class="justify-end fixed-actions">
      <v-btn text @click="dialogNotice = false">{{ $t('common.cancel') }}</v-btn>
      <v-btn color="primary" @click="saveNotice">{{ $t('notices.saveNotice') }}</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>


    <!-- 🔹 MODAL VER AVISO COMPLETO -->
    <v-dialog v-model="dialogViewNotice" max-width="600px">
  <v-card class="dialog-scroll">
    <v-card-title class="text-h6 font-weight-bold">
      {{ viewNoticeData.title }}
    </v-card-title>

    <v-card-subtitle>
      {{ formatDate(viewNoticeData.date) }}
    </v-card-subtitle>

    <v-card-text class="dialog-content">
      <p class="text-body-1">
        {{ viewNoticeData.description }}
      </p>
    </v-card-text>

    <v-card-actions class="justify-end fixed-actions">
      <v-btn text @click="dialogViewNotice = false">{{ $t('common.close') }}</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>


    <!-- 🔹 DIÁLOGO DE CONFIRMACIÓN -->
    <v-dialog v-model="dialogConfirm" max-width="420px">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">{{ $t('common.confirmAction') }}</v-card-title>

        <v-card-text>
          {{ confirmMessage }}
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="dialogConfirm = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="red" dark @click="confirmAction(); dialogConfirm = false">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>


<script>
import { db } from "@/firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { sendCompanyPushNotification } from "@/services/pushNotifications";
import { getUserErrorMessage } from "@/utils/errors";

export default {
  name: "Notices",

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    companyId() {
      return this.$store.getters["auth/companyId"];
    },
    user() {
      return this.$store.getters["auth/user"];
    },
    rules() {
      return {
        required: (value) => !!value || this.$t("common.required"),
      };
    },
  },

  data() {
    return {
      boards: [],
      message: "",
      messageType: "success",

      dialogBoard: false,
      dialogNotice: false,
      dialogViewNotice: false,
      dialogConfirm: false,
      boardValid: false,
      noticeValid: false,

      confirmMessage: "",
      confirmAction: null,

      editingBoard: null,
      editingNotice: null,

      newBoardTitle: "",
      newNoticeTitle: "",
      newNoticeDescription: "",
      currentBoardId: null,

      viewNoticeData: {},
    };
  },

  async created() {
    await this.loadBoards();
  },

  methods: {
    formatDate(date) {
      if (!date) return "";
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString(this.$i18n.locale);
    },

    async loadBoards() {
      if (!this.companyId) return;

      try {
        this.message = "";
        const boardsRef = collection(db, "companies", this.companyId, "boards");
        const qBoards = query(boardsRef, orderBy("createdAt", "asc"));
        const boardsSnap = await getDocs(qBoards);

        this.boards = [];

        for (const b of boardsSnap.docs) {
          const noticesRef = collection(
            db,
            "companies",
            this.companyId,
            "boards",
            b.id,
            "notices"
          );
          const qNotices = query(noticesRef, orderBy("date", "desc"));
          const noticesSnap = await getDocs(qNotices);

          this.boards.push({
            id: b.id,
            ...b.data(),
            notices: noticesSnap.docs.map((n) => ({ id: n.id, ...n.data() })),
          });
        }
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "notices.loadError", this.$i18n), "error");
      }
    },

    /* 🚀 DIÁLOGO DE CONFIRMACIÓN */
    openConfirm(message, action) {
      this.confirmMessage = message;
      this.confirmAction = action;
      this.dialogConfirm = true;
    },

    /* --- TABLONES --- */

    editBoard(board) {
      this.editingBoard = board.id;
      this.newBoardTitle = board.title;
      this.dialogBoard = true;
      this.$nextTick(() => this.$refs.boardForm?.resetValidation());
    },

    async saveBoard() {
      if (!this.$refs.boardForm.validate()) return;

      try {
        if (this.editingBoard) {
          await updateDoc(
            doc(db, "companies", this.companyId, "boards", this.editingBoard),
            { title: this.newBoardTitle }
          );

          // 🔥 Actualización local
          const board = this.boards.find(b => b.id === this.editingBoard);
          if (board) board.title = this.newBoardTitle;

        } else {
          const ref = await addDoc(
            collection(db, "companies", this.companyId, "boards"),
            {
              title: this.newBoardTitle,
              createdByUid: this.user?.uid || "",
              createdByEmail: this.user?.email || "",
              createdAt: new Date(),
            }
          );

          // 🔥 Insertar localmente
          this.boards.push({
            id: ref.id,
            title: this.newBoardTitle,
            createdByUid: this.user?.uid || "",
            createdByEmail: this.user?.email || "",
            createdAt: new Date(),
            notices: []
          });
        }

        this.closeBoardDialog();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "notices.saveBoardError", this.$i18n), "error");
      }
    },

    closeBoardDialog() {
      this.newBoardTitle = "";
      this.editingBoard = null;
      this.dialogBoard = false;
      this.$nextTick(() => this.$refs.boardForm?.resetValidation());
    },

    deleteBoard(boardId) {
      this.openConfirm(this.$t("notices.deleteBoardConfirm"), async () => {
        try {
          // Eliminar avisos
          const noticesRef = collection(
            db, "companies", this.companyId, "boards", boardId, "notices"
          );
          const noticesSnap = await getDocs(noticesRef);

          for (const n of noticesSnap.docs) {
            await deleteDoc(n.ref);
          }

          // Eliminar tablón
          await deleteDoc(
            doc(db, "companies", this.companyId, "boards", boardId)
          );

          // 🔥 Actualización local
          this.boards = this.boards.filter(b => b.id !== boardId);
        } catch (error) {
          this.showMessage(getUserErrorMessage(error, "notices.deleteBoardError", this.$i18n), "error");
        }
      });
    },

    /* --- AVISOS --- */

    openNoticeDialog(boardId) {
      this.currentBoardId = boardId;
      this.newNoticeTitle = "";
      this.newNoticeDescription = "";
      this.editingNotice = null;
      this.dialogNotice = true;
      this.$nextTick(() => this.$refs.noticeForm?.resetValidation());
    },

    editNotice(boardId, notice) {
      this.editingNotice = notice.id;
      this.currentBoardId = boardId;
      this.newNoticeTitle = notice.title;
      this.newNoticeDescription = notice.description;
      this.dialogNotice = true;
      this.$nextTick(() => this.$refs.noticeForm?.resetValidation());
    },

    async saveNotice() {
      if (!this.$refs.noticeForm.validate()) return;

      const board = this.boards.find(b => b.id === this.currentBoardId);

      try {
        if (this.editingNotice) {
          await updateDoc(
            doc(
              db,
              "companies",
              this.companyId,
              "boards",
              this.currentBoardId,
              "notices",
              this.editingNotice
            ),
            {
              title: this.newNoticeTitle,
              description: this.newNoticeDescription,
            }
          );

          // 🔥 Actualizar local
          const notice = board.notices.find(n => n.id === this.editingNotice);
          if (notice) {
            notice.title = this.newNoticeTitle;
            notice.description = this.newNoticeDescription;
          }

        } else {
          const ref = await addDoc(
            collection(
              db,
              "companies",
              this.companyId,
              "boards",
              this.currentBoardId,
              "notices"
            ),
            {
              title: this.newNoticeTitle,
              description: this.newNoticeDescription,
              createdByUid: this.user?.uid || "",
              createdByEmail: this.user?.email || "",
              date: new Date(),
            }
          );

          // 🔥 Insertar local
          board.notices.unshift({
            id: ref.id,
            title: this.newNoticeTitle,
            description: this.newNoticeDescription,
            createdByUid: this.user?.uid || "",
            createdByEmail: this.user?.email || "",
            date: new Date(),
          });

          sendCompanyPushNotification(this.companyId, {
            notificationType: "notice",
            title: this.$t("notices.notificationTitle"),
            body: this.newNoticeTitle,
            data: {
              companyId: this.companyId,
              boardId: this.currentBoardId,
              noticeId: ref.id,
            },
          }).catch((error) => {
            console.warn(this.$t("notices.notificationError"), error);
          });
        }

        this.dialogNotice = false;
        this.editingNotice = null;
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "notices.saveNoticeError", this.$i18n), "error");
      }
    },

    deleteNotice(boardId, noticeId) {
      this.openConfirm(this.$t("notices.deleteNoticeConfirm"), async () => {
        try {
          await deleteDoc(
            doc(
              db,
              "companies",
              this.companyId,
              "boards",
              boardId,
              "notices",
              noticeId
            )
          );

          // 🔥 Actualizar local
          const board = this.boards.find(b => b.id === boardId);
          if (board) {
            board.notices = board.notices.filter(n => n.id !== noticeId);
          }
        } catch (error) {
          this.showMessage(getUserErrorMessage(error, "notices.deleteNoticeError", this.$i18n), "error");
        }
      });
    },

    openViewNotice(notice) {
      this.viewNoticeData = notice;
      this.dialogViewNotice = true;
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    },
  },
};
</script>


<style scoped>
.board-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #3a3551;
}

.notice-card {
  background-color: #f8f7fd;
  border-radius: 14px;
  width: 360px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
}

.notice-card-header {
  display: flex;
  align-items: center;
}

.notice-card-heading {
  min-width: 0;
}

.notice-title {
  overflow-wrap: anywhere;
}

.notice-description {
  color: #555;
  overflow: hidden;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}

.see-more-btn {
  color: #999 !important;
  font-size: 0.7rem !important;
  min-height: 20px !important;
  padding: 0 !important;
  text-transform: none;
}

.text-grey {
  color: #777;
}

.v-btn.fixed {
  bottom: 24px !important;
  right: 24px !important;
  position: fixed !important;
}

.notice-slider .v-slide-group__wrapper {
  min-height: 150px !important;
  display: flex;
  align-items: center;
}

.dialog-scroll {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Textarea con scroll independiente */
.scroll-textarea textarea {
  max-height: 300px !important;
  overflow-y: auto !important;
}

/* Mantener actions fijos abajo */
.fixed-actions {
  border-top: 1px solid #eee;
  padding-top: 8px;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 5;
}

.mobile-board-panel {
  background: #f8f7fd !important;
  border-color: rgba(74, 58, 255, 0.12) !important;
  border-radius: 14px;
  margin-bottom: 14px;
  padding: 12px;
}

.mobile-board-header {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 10px;
}

.mobile-board-title {
  color: #3a3551;
  font-size: 1.08rem;
  font-weight: 900;
  line-height: 1.25;
  margin: 0;
  overflow-wrap: anywhere;
}

.mobile-board-count {
  color: #756f86;
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 3px;
}

.mobile-board-menu {
  color: #5f5870;
  flex: 0 0 auto;
  margin-top: -4px;
}

.mobile-board-actions {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
  margin-bottom: 12px;
}

.mobile-board-action {
  letter-spacing: 0;
  min-width: 0 !important;
  width: 100%;
}

.mobile-board-action--primary {
  grid-column: auto;
}

.mobile-notice-list {
  display: grid;
  gap: 10px;
}

.mobile-notice-item {
  background: #fff;
  border: 1px solid rgba(63, 61, 86, 0.08);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(63, 61, 86, 0.05);
  padding: 12px;
}

.mobile-notice-row {
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
}

.mobile-notice-content {
  min-width: 0;
}

.mobile-notice-menu {
  align-self: start;
  color: #5f5870;
  margin-top: -4px;
}

.mobile-notice-date {
  color: #807894;
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.mobile-notice-title {
  color: #2f2d40;
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.25;
  margin: 0 0 6px;
  overflow-wrap: anywhere;
}

.mobile-notice-description {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: #555;
  display: -webkit-box;
  font-size: 0.9rem;
  line-height: 1.45;
  margin: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.notice-empty-mobile {
  background: #fff;
  border: 1px dashed rgba(74, 58, 255, 0.18);
  border-radius: 12px;
  color: #777;
  font-size: 0.9rem;
  padding: 18px 12px;
  text-align: center;
}

@media (max-width: 960px) {
  .v-container {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  .v-btn.fixed {
    bottom: 18px !important;
    right: 18px !important;
  }

  ::v-deep .v-dialog {
    margin: 12px;
  }

  .dialog-scroll {
    max-height: calc(100vh - 24px);
  }

  .fixed-actions {
    align-items: stretch;
    flex-direction: column-reverse;
    gap: 8px;
    padding: 10px 16px !important;
  }

  .fixed-actions .v-btn {
    margin-left: 0 !important;
    width: 100%;
  }
}

</style>
